/**
 * weather.ts — the WeatherSource.
 *
 * Talks to the Weather Gateway (`/geocode`, `/weather`), maps a `WeatherResponse`
 * onto the engine via `setWeather(...)` (deriving `localHour` from the returned
 * timezone), and re-fetches on an interval.
 *
 * Offline / failure fallback: the prototype's simulated-drift generator. When
 * there is no gateway URL, no network, or a fetch error, the chime keeps
 * breathing on simulated wind — it must never go dead.
 */
import type { Engine } from '@windchime/engine';
import { localHour } from '@windchime/shared';
import type { GeocodeResult, WeatherResponse, WeatherSnapshot } from '@windchime/shared';

export type WeatherMode = 'live' | 'sim';

export interface WeatherStatus {
  mode: WeatherMode;
  source: 'nws' | 'open-meteo' | 'simulated';
  /** place label, live mode only */
  label?: string;
  /** the selected place's country (live mode) — drives the wind-unit default */
  country?: string;
  station?: string | null;
  distanceKm?: number;
  /** informational note (e.g. why we're simulating) */
  note?: string;
  /** failure reason, if a fetch fell back to simulated */
  error?: string;
  snapshot: WeatherSnapshot;
}

const REFRESH_MS = 10 * 60 * 1000; // re-fetch weather every ~10 min (gateway caches too)
const CLOCK_MS = 60 * 1000; // advance localHour every minute so activity drifts smoothly

const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));

/** Great-circle distance in km — used to sort geocode matches by proximity. */
export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLon - aLon) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
const deviceHour = (): number => {
  const d = new Date();
  return d.getHours() + d.getMinutes() / 60;
};
const placeLabel = (p: GeocodeResult): string => [p.name, p.admin1, p.country].filter(Boolean).join(', ');

export class WeatherSource {
  private base?: string;
  private mode: WeatherMode = 'sim';
  private current: WeatherSnapshot = {
    windSpeed: 18,
    windDirFrom: 225,
    gust: 1.4,
    tempF: 72,
    localHour: deviceHour(),
  };
  private statusCb: (s: WeatherStatus) => void = () => {};
  private src: Omit<WeatherStatus, 'mode' | 'snapshot'> = { source: 'simulated' };

  // live-mode bookkeeping
  private live?: { lat: number; lon: number; timezone: string; label?: string; country?: string };
  private refetchTimer?: ReturnType<typeof setInterval>;
  private clockTimer?: ReturnType<typeof setInterval>;

  // sim-mode drift accumulators (ported from the prototype's driftWeather)
  private dgt = { ws: 0, dir: 0, g: 0, t: 0 };

  constructor(
    private engine: Engine,
    gatewayUrl?: string,
  ) {
    this.base = (gatewayUrl ?? '').trim().replace(/\/+$/, '') || undefined;
  }

  get gatewayConfigured(): boolean {
    return !!this.base;
  }
  get snapshot(): WeatherSnapshot {
    return this.current;
  }
  /** Weather inputs the renderer needs (not part of ViewState). */
  renderWeather(): { tempF: number; windDirFrom: number } {
    return { tempF: this.current.tempF, windDirFrom: this.current.windDirFrom };
  }

  onStatus(cb: (s: WeatherStatus) => void): () => void {
    this.statusCb = cb;
    this.emit();
    return () => {
      this.statusCb = () => {};
    };
  }

  // --- simulated fallback ---------------------------------------------------

  startSimulated(note?: string): void {
    this.mode = 'sim';
    this.clearTimers();
    this.src = { source: 'simulated', note };
    this.engine.setWeather(this.current); // ensure the engine has the current sim snapshot
    this.emit();
  }

  /** Called every animation frame. Only does work in simulated mode. */
  frame(dt: number): void {
    if (this.mode !== 'sim') return;
    // gentle random-walk for an ambient, hands-off demo (ported from prototype)
    const d = this.dgt;
    d.ws += (Math.random() - 0.5) * dt * 2.2;
    d.ws *= 0.96;
    d.dir += (Math.random() - 0.5) * dt * 9;
    d.dir *= 0.97;
    d.g += (Math.random() - 0.5) * dt * 0.25;
    d.g *= 0.95;
    d.t += (Math.random() - 0.5) * dt * 1.4;
    d.t *= 0.97;
    const c = this.current;
    c.windSpeed = clamp(c.windSpeed + d.ws, 0, 80);
    c.windDirFrom = (c.windDirFrom + d.dir + 360) % 360;
    c.gust = clamp(c.gust + d.g, 1, 2);
    c.tempF = clamp(c.tempF + d.t, 5, 100);
    c.localHour = deviceHour();
    this.engine.setWeather(c);
  }

  // --- gateway -------------------------------------------------------------

  /** The point "near the user", for proximity-sorting search results: the live
   *  location they're already listening to, if any. */
  get referencePoint(): { lat: number; lon: number } | null {
    return this.live ? { lat: this.live.lat, lon: this.live.lon } : null;
  }

  /** GET /geocode?q= — returns candidate places (throws if no gateway).
   *  Pass an AbortSignal so superseded type-ahead requests can be cancelled. */
  async geocode(q: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
    if (!this.base) throw new Error('no gateway configured');
    const res = await fetch(`${this.base}/geocode?q=${encodeURIComponent(q)}&count=8`, { signal });
    if (!res.ok) throw new Error(`geocode failed (${res.status})`);
    const body = (await res.json()) as { results?: GeocodeResult[]; error?: string };
    if (body.error) throw new Error(body.error);
    return body.results ?? [];
  }

  /** Pick a geocoded place and switch to live weather there. */
  async selectPlace(p: GeocodeResult): Promise<void> {
    await this.useCoords(p.lat, p.lon, placeLabel(p), p.timezone, p.country);
  }

  /** Switch to live weather for a coordinate and start the refresh cycle. */
  async useCoords(lat: number, lon: number, label?: string, tzHint?: string, country?: string): Promise<void> {
    if (!this.base) {
      this.startSimulated('no gateway configured — set VITE_GATEWAY_URL for live weather');
      throw new Error('no gateway configured');
    }
    this.mode = 'live';
    this.live = { lat, lon, timezone: tzHint ?? 'UTC', label, country };
    this.clearTimers();
    await this.fetchOnce();
    this.refetchTimer = setInterval(() => void this.fetchOnce(), REFRESH_MS);
    this.clockTimer = setInterval(() => this.refreshClock(), CLOCK_MS);
  }

  private async fetchOnce(): Promise<void> {
    const live = this.live;
    if (!live || !this.base) return;
    try {
      const res = await fetch(`${this.base}/weather?lat=${live.lat}&lon=${live.lon}`);
      if (!res.ok) throw new Error(`weather failed (${res.status})`);
      const wr = (await res.json()) as WeatherResponse & { error?: string };
      if (wr.error) throw new Error(wr.error);
      live.timezone = wr.timezone || live.timezone;
      const snap: WeatherSnapshot = {
        windSpeed: wr.windSpeed,
        windDirFrom: wr.windDirFrom,
        gust: wr.gust,
        tempF: wr.tempF,
        localHour: localHour(live.timezone),
      };
      this.current = snap;
      this.engine.setWeather(snap);
      this.src = {
        source: wr.source,
        label: live.label ?? wr.location.label,
        country: live.country,
        station: wr.station?.name ?? null,
        distanceKm: wr.station?.distanceKm,
      };
      this.emit();
    } catch (err) {
      // never go dead: drop back to simulated drift and surface the reason
      this.mode = 'sim';
      this.clearTimers();
      this.src = {
        source: 'simulated',
        label: live.label,
        error: String((err as Error)?.message ?? err),
      };
      this.emit();
    }
  }

  /** Advance localHour from the cached timezone without a full re-fetch. */
  private refreshClock(): void {
    if (this.mode !== 'live' || !this.live) return;
    this.current = { ...this.current, localHour: localHour(this.live.timezone) };
    this.engine.setWeather({ localHour: this.current.localHour });
    this.emit();
  }

  stop(): void {
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.refetchTimer) {
      clearInterval(this.refetchTimer);
      this.refetchTimer = undefined;
    }
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = undefined;
    }
  }

  private emit(): void {
    this.statusCb({ mode: this.mode, snapshot: this.current, ...this.src });
  }
}
